import React, { useState, useEffect } from "react";
import Layout from "../../Components/Layout";
import SearchFilters from "./SearchFilters";
import CalendarView from "./CalendarView";
import AppointmentModal from "./AddAppointment";
import PatientAppointmentsTable from "./PatientAppointmentsTable";
import { ErrorSuccessModal } from "../../Modals/ErrorSuccessModal";
import { useRvs } from "../../hooks/useRv";
import { RvDto } from "../../types/rv";
import { SearchFilterState } from "../../types/appointment";

export interface CalendarAppointment {
  id: string;
  patientName: string;
  amka: string;
  title: string;
  start: string;
  end: string;
  encounterData: RvDto;
}

const AppointmentsPage: React.FC = () => {
  useEffect(() => {
    const savedTheme = localStorage.getItem("app-theme") || "teal";
    document.documentElement.setAttribute("data-theme", savedTheme);
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Προσθέσαμε το modalState και το closeModal από το hook `useRvs()`
  const { rvs, fetchRvs, createRv, isLoading, modalState, closeModal } = useRvs();

  const [filters, setFilters] = useState<SearchFilterState>({
    firstName: "",
    lastName: "",
    amka: "",
    patientId: undefined,
  } as any);

  useEffect(() => {
    fetchRvs();
  }, []);

  const appointments: CalendarAppointment[] = (rvs || []).map((rv) => {
    const dateStr = rv.day || new Date().toISOString().split("T")[0];
    const timeStr = rv.startTime || "09:00";
    const startDateTime = new Date(`${dateStr}T${timeStr}`);
    const endDateTime = rv.endTime 
      ? new Date(`${dateStr}T${rv.endTime}`) 
      : new Date(startDateTime.getTime() + 30 * 60000);

    const patientFullName = `${rv.patientLastName || ""} ${rv.patientFirstName || ""}`.trim() || "Ασθενής";

    return {
      id: String(rv.rvId || Math.random()),
      patientName: patientFullName,
      amka: "",
      title: `${patientFullName} - ${rv.rvReason || "Ραντεβού"}`,
      start: startDateTime.toISOString(),
      end: endDateTime.toISOString(),
      encounterData: rv,
    };
  });

const handleCreateAppointment = async (data: Partial<RvDto>) => {
    try {
      await createRv(data as any);
      fetchRvs(); // Ανανέωση λίστας μόνο αν αποθηκευτεί επιτυχώς
    } catch (error) {
      // Το σφάλμα πιάνεται αυτόματα από το hook και εμφανίζεται στο modal
      console.error("Σφάλμα κατά τη δημιουργία ραντεβού", error);
    }
  };

  const filteredAppointments = appointments.filter((app) => {
    if (filters.patientId && Number(app.encounterData.patientId) !== Number(filters.patientId)) {
      return false;
    }
    return true;
  });

  return (
    <Layout>
      <div className="flex flex-col gap-4 h-[calc(100vh-100px)]">
        <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <h1 className="text-2xl font-bold text-gray-800">
            Διαχείριση Ραντεβού
          </h1>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-bold hover:bg-darkprimary transition-colors"
          >
            <span className="text-xl leading-none">+</span> Νέο Ραντεβού
          </button>
        </div>

        <div className="flex gap-6 flex-1 overflow-hidden">
          <aside className="w-80 bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-6">
            <h2 className="text-xl font-bold">Φίλτρα Αναζήτησης</h2>
            <SearchFilters onSearch={setFilters} />
          </aside>

          <main className="flex-1 bg-white p-6 rounded-xl border border-gray-200 shadow-sm overflow-hidden relative">
            {isLoading && (
              <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 text-sm font-semibold">
                Φόρτωση δεδομένων...
              </div>
            )}
            
            {filters.patientId ? (
              <PatientAppointmentsTable 
                appointments={appointments} 
                patientId={Number(filters.patientId)} 
              />
            ) : (
              <CalendarView events={filteredAppointments} />
            )}
          </main>
        </div>
      </div>

      <AppointmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateAppointment}
      />

      {/* Το ErrorSuccessModal για την εμφάνιση μυνημάτων επιτυχίας/σφάλματος */}
      <ErrorSuccessModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
      />
    </Layout>
  );
};

export default AppointmentsPage;